import { useDebouncedValue } from "@tanstack/react-pacer/debouncer";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";

import { useTeamAliases } from "@/hooks/useTeamAliases";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import type {
  DriverDutyStatusFilter,
  DriverFilterConfig,
  DriverSearchTarget,
  DriverSortKey,
  DriverStatusFilter,
  DriverViewTab,
  DriverVolumeFilter,
  LocationFilter,
} from "@/types/driver";

export function useDriverFilters(
  workspaceId: string,
  workspaceMembershipId: string,
  defaultLanguage = "en",
  unactivatedMembershipIds: string[] = [],
  searchTargets: readonly DriverSearchTarget[] = [{ kind: "column", field: "name" }],
  personalDeviceMembershipIds: string[] = [],
) {
  const { team, availableTeams, teamAliases } = useTeamAliases(workspaceId, workspaceMembershipId);

  const { settings, updateWebSettings } = useWebSettings();
  const tab = settings.driverView.tab as DriverViewTab;
  const sort = settings.driverView.sort as DriverSortKey;
  const volumeFilter = settings.driverView.volumeFilter as DriverVolumeFilter[];

  const setTabRaw = useCallback(
    (value: SetStateAction<DriverViewTab>) => {
      const next = typeof value === "function" ? (value as (prev: DriverViewTab) => DriverViewTab)(tab) : value;
      updateWebSettings({ driverView: { tab: next } });
    },
    [tab, updateWebSettings],
  );

  const setSort = useCallback(
    (value: SetStateAction<DriverSortKey>) => {
      const next = typeof value === "function" ? (value as (prev: DriverSortKey) => DriverSortKey)(sort) : value;
      updateWebSettings({ driverView: { sort: next } });
    },
    [sort, updateWebSettings],
  );

  const [statusFilter, setStatusFilter] = useState<DriverStatusFilter[]>([]);
  const [dutyStatusFilter, setDutyStatusFilter] = useState<DriverDutyStatusFilter[]>([]);

  const setVolumeFilter = useCallback(
    (value: SetStateAction<DriverVolumeFilter[]>) => {
      const next = typeof value === "function" ? value(volumeFilter) : value;
      updateWebSettings({ driverView: { volumeFilter: next } });
    },
    [volumeFilter, updateWebSettings],
  );
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [aliasNames, setAliasNames] = useState<string[]>([]);
  const [location, setLocation] = useState<LocationFilter | null>(null);
  const [search, setSearch] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const clearAll = useCallback(() => {
    setStatusFilter([]);
    setVolumeFilter([]);
    setDutyStatusFilter([]);
    setGroupIds([]);
    setAliasNames([]);
    setLocation(null);
    setShowUnreadOnly(false);
  }, [setVolumeFilter]);

  const setTab: Dispatch<SetStateAction<DriverViewTab>> = useCallback(
    (value) => {
      clearAll();
      setTabRaw(value);
    },
    [setTabRaw, clearAll],
  );
  const [debouncedSearch] = useDebouncedValue(search, { wait: 300 });

  const toggleStatusFilter = useCallback((value: DriverStatusFilter) => {
    setStatusFilter((prev) => {
      if (value === "never-logged-in") {
        return prev.includes("never-logged-in") ? [] : ["never-logged-in"];
      }
      const withoutNever = prev.filter((v) => v !== "never-logged-in");
      return withoutNever.includes(value) ? withoutNever.filter((v) => v !== value) : [...withoutNever, value];
    });
  }, []);

  const clearStatusFilter = useCallback(() => {
    setStatusFilter([]);
  }, []);

  const toggleVolumeFilter = useCallback(
    (value: DriverVolumeFilter) => {
      setVolumeFilter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    },
    [setVolumeFilter],
  );

  const clearVolumeFilter = useCallback(() => {
    setVolumeFilter([]);
  }, [setVolumeFilter]);

  const toggleDutyStatusFilter = useCallback((value: DriverDutyStatusFilter) => {
    setDutyStatusFilter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }, []);

  const clearDutyStatusFilter = useCallback(() => {
    setDutyStatusFilter([]);
  }, []);

  const toggleGroupId = useCallback((id: string) => {
    setGroupIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const selectGroupIds = useCallback((ids: string[]) => {
    setGroupIds((prev) => {
      const current = new Set(prev);
      for (const id of ids) current.add(id);
      return [...current];
    });
  }, []);

  const clearGroupIds = useCallback(() => {
    setGroupIds([]);
  }, []);

  const toggleAliasName = useCallback((name: string) => {
    setAliasNames((prev) => (prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name]));
  }, []);

  const clearAliasNames = useCallback(() => {
    setAliasNames([]);
  }, []);

  const clearLocation = useCallback(() => {
    setLocation(null);
  }, []);

  const toggleUnreadOnly = useCallback(() => {
    setShowUnreadOnly((prev) => !prev);
  }, []);

  const hasActiveFilters =
    statusFilter.length > 0 ||
    volumeFilter.length > 0 ||
    dutyStatusFilter.length > 0 ||
    groupIds.length > 0 ||
    aliasNames.length > 0 ||
    location !== null ||
    showUnreadOnly;

  const effectiveTab = tab;

  const config: DriverFilterConfig = useMemo(
    () => ({
      tab: effectiveTab,
      sort,
      team,
      teamAliases,
      statusFilter,
      volumeFilter,
      dutyStatusFilter,
      groupIds,
      aliasNames,
      location,
      search: debouncedSearch,
      searchTargets,
      language: defaultLanguage,
      unactivatedMembershipIds,
      personalDeviceMembershipIds,
    }),
    [
      effectiveTab,
      team,
      teamAliases,
      sort,
      statusFilter,
      volumeFilter,
      dutyStatusFilter,
      groupIds,
      aliasNames,
      location,
      debouncedSearch,
      searchTargets,
      defaultLanguage,
      unactivatedMembershipIds,
      personalDeviceMembershipIds,
    ],
  );

  return {
    config,
    tab,
    effectiveTab,
    setTab,
    team,
    availableTeams,
    sort,
    setSort,
    statusFilter,
    toggleStatusFilter,
    clearStatusFilter,
    volumeFilter,
    toggleVolumeFilter,
    clearVolumeFilter,
    dutyStatusFilter,
    toggleDutyStatusFilter,
    clearDutyStatusFilter,
    groupIds,
    toggleGroupId,
    selectGroupIds,
    clearGroupIds,
    aliasNames,
    toggleAliasName,
    clearAliasNames,
    location,
    setLocation,
    clearLocation,
    search,
    setSearch,
    hasActiveFilters,
    clearAll,
    showUnreadOnly,
    toggleUnreadOnly,
  };
}
